<!--
  @component QuotaMeter — small usage-vs-limit progress bar.
  tier:      primitive (visual atom)
  purpose:   the consumer-facing "you've used X / Y" indicator
             (compute · bandwidth · storage · rows · …).
             Reused by ProjectCard, Overview, Billing.

  Color shifts to warning/error past 75% / 95% so operators see
  a spend cliff coming visually.
-->
<script lang="ts">
  interface Props {
    label: string;
    used: number;
    limit: number;
    /** Unit suffix shown in the numeric ("3.2 / 10 GB"). */
    unit?: string;
    /** Compact: just label + bar (no numeric line). */
    compact?: boolean;
  }
  const { label, used, limit, unit, compact = false }: Props = $props();

  // Clamped ratio + numeric display.
  const ratio = $derived(
    limit <= 0 ? 0 : Math.max(0, Math.min(1, used / limit)),
  );
  const pct = $derived(Math.round(ratio * 100));
  const tone = $derived(
    ratio >= 0.95 ? "error" : ratio >= 0.75 ? "warn" : "ok",
  );
  const numeric = $derived(
    `${used.toLocaleString()} / ${limit.toLocaleString()}${unit ? ` ${unit}` : ""}`,
  );
</script>

<div class="meter" data-tone={tone}>
  <div class="head">
    <span class="label">{label}</span>
    {#if !compact}<span class="numeric">{numeric}</span>{/if}
  </div>
  <div class="bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="{label}: {pct}%">
    <div class="fill" style:width="{pct}%"></div>
  </div>
</div>

<style>
  .meter {
    display: flex;
    flex-direction: column;
    gap: var(--space-1, 4px);
    font-family: var(--type-font);
    width: 100%;
  }
  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-2);
  }
  .label {
    font-size: 11px;
    color: var(--text-tertiary);
    text-transform: lowercase;
  }
  .numeric {
    font-size: 11px;
    font-family: var(--type-mono);
    color: var(--text-secondary);
  }
  .meter[data-tone="warn"] .numeric { color: var(--warning); }
  .meter[data-tone="error"] .numeric { color: var(--error); }
  .bar {
    height: 4px;
    background: var(--glass-fill);
    border-radius: var(--radius-pill);
    overflow: hidden;
  }
  .fill {
    height: 100%;
    background: var(--accent);
    border-radius: var(--radius-pill);
    transition: width var(--ease-normal);
  }
  .meter[data-tone="warn"] .fill { background: var(--warning); }
  .meter[data-tone="error"] .fill { background: var(--error); }
</style>
